import React, { useContext, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context';
import { useCampaignsByProductId } from '../../../../hooks/campaigns-by-product-id';
import {
  useCompareProductsSelection,
  SelectProductToCompareById,
  DeselectProductToCompareById,
} from '../../../../hooks/compare-products-selection';
import { AppState } from '../../../../reducers';
import { ViewportContext } from '../../../../components/viewport';
import CompareProductsBasket from '../../../../components/compare-products-basket';
import CompareProductsToggle from '../../../../components/compare-products-toggle';
import {
  ProductsSlice,
  AdvizedContentFragment,
  ProductCollection,
} from '../../../../types';
import { LayoutMode } from '../../types';
import { TableLayout } from './layout-table';
import { ListLayout } from './layout-list';
import { GridLayout } from './layout-grid';
import ProductOfferContainer from '../productOffersContainer';
import { setProductCollectionLayout as setProductCollectionLayoutAction } from '../../actions';
import { selectProductLayout } from '../../selectors';
import { getSortKeyForProperty } from '../../utils/properties';
import { getPropertyColumnName } from '../../utils/property-columns';
import { ProductComparisonProps } from './types';
import * as S from './styles';

type Props = {
  layout: LayoutMode;
  advized: AdvizedContentFragment;
  setProductCollectionLayout: (mode: LayoutMode) => void;
  productCollection: ProductCollection;
} & Pick<
  ProductsSlice,
  | 'id'
  | 'pageInfo'
  | 'products'
  | 'productsCount'
  | 'storeCount'
  | 'legalText'
  | 'availableSorts'
  | 'currentSort'
  | 'maxHitsAmount'
  | 'sortDirection'
  | 'propertyColumns'
>;

const Products = ({
  products,
  currentSort,
  sortDirection,
  propertyColumns,
  availableSorts,
  id,
  layout,
  setProductCollectionLayout,
  advized,
  productCollection,
  ...productContainerProps
}: Props) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: `productCollection:layout:${layout}`,
    });
  }, [layout, id, dispatch]);
  const { isMobile } = useContext(ViewportContext);
  const { linkTo, t } = useAppContext();
  const {
    isSelectingProducts,
    selectedProducts,
    startProductSelection,
    abortProductSelection,
    selectProduct,
    deselectProduct,
  } = useCompareProductsSelection();

  const abortProductSelectionFromCompareButton = useCallback(
    () => abortProductSelection('compareButton'),
    [abortProductSelection]
  );
  const abortProductSelectionFromBasket = useCallback(
    () => abortProductSelection('basket'),
    [abortProductSelection]
  );

  const selectProductById = useCallback<SelectProductToCompareById>(
    (productId, source) => {
      const selectedProduct = products.find(p => p.id === productId);
      if (selectedProduct) {
        selectProduct(source, selectedProduct);
      }
    },
    [products, selectProduct]
  );

  const deselectProductById = useCallback<DeselectProductToCompareById>(
    (productId, source) => {
      const deselectedProduct = products.find(p => p.id === productId);
      if (deselectedProduct) {
        deselectProduct(source, deselectedProduct);
      }
    },
    [products, deselectProduct]
  );

  const availableSortOptions = [...availableSorts]
    .concat('property.in_stock')
    .concat(
      propertyColumns.map(column => column.id).map(getSortKeyForProperty)
    );

  const availableSortOptionsTranslationTable = propertyColumns.reduce(
    (prev, item) => {
      // eslint-disable-next-line no-param-reassign
      prev[item.id] = getPropertyColumnName(item, t);
      return prev;
    },
    {}
  );

  /**
   * Select a suitable category to use as the "comparison category", ie. the context within which
   * you're comparing products.
   */
  const { categories, categoryPath } = productCollection;
  const comparisonCategory =
    categories?.length === 1
      ? // If the product collection represents exactly one category, use that
        categories[0]
      : // Otherwise, use the nearest parent category as determined by the category path
        categoryPath[categoryPath.length - 1];

  const compareProductsToggle = useMemo(
    () => (
      <CompareProductsToggle
        active={isSelectingProducts}
        activate={startProductSelection}
        deactivate={abortProductSelectionFromCompareButton}
      />
    ),
    [
      isSelectingProducts,
      startProductSelection,
      abortProductSelectionFromCompareButton,
    ]
  );

  const productComparisonProps: ProductComparisonProps = {
    isSelectingProducts,
    selectedProducts,
    selectProductById,
    deselectProductById,
  };

  const supportedViewModes = useMemo(
    () => [LayoutMode.GRID, LayoutMode.LIST],
    []
  );

  const productIds = useMemo(() => products.map(p => p.id), [products]);
  const campaignsByProduct = useCampaignsByProductId(productIds);

  const hasAdvizedContent =
    advized && advized.selected && productContainerProps.pageInfo?.offset === 0;

  const listView = isMobile ? (
    <ListLayout
      products={products}
      {...productComparisonProps}
      campaignsByProduct={campaignsByProduct}
    />
  ) : (
    <TableLayout
      propertyColumns={propertyColumns}
      {...{
        products,
        currentSort,
        sortDirection,
      }}
      {...productComparisonProps}
      campaignsByProduct={campaignsByProduct}
    />
  );
  return (
    <S.ProductsWrapper>
      <ProductOfferContainer
        {...{ ...productContainerProps, id, currentSort }}
        layout={layout}
        onLayoutChange={setProductCollectionLayout}
        compareProductsToggle={compareProductsToggle}
        availableSorts={availableSortOptions}
        availableSortsTranslations={availableSortOptionsTranslationTable}
        supportedViewModes={supportedViewModes}
      >
        {layout === LayoutMode.GRID ? (
          <GridLayout
            advized={hasAdvizedContent ? advized : null}
            products={products}
            campaignsByProduct={campaignsByProduct}
            {...productComparisonProps}
          />
        ) : (
          listView
        )}
      </ProductOfferContainer>

      {isSelectingProducts && (
        <CompareProductsBasket
          products={selectedProducts}
          compareUrl={
            selectedProducts.length > 1
              ? linkTo('productCompare', {
                  category: comparisonCategory,
                  products: selectedProducts,
                })
              : undefined
          }
          onEmpty={abortProductSelectionFromBasket}
          onProductDelete={deselectProductById}
        />
      )}
    </S.ProductsWrapper>
  );
};

const dispatchActions = {
  setProductCollectionLayout: setProductCollectionLayoutAction,
};

const mapStateToProps = (state: AppState) => ({
  layout: selectProductLayout(state),
});

export default connect(mapStateToProps, dispatchActions)(Products);
